<template>
  <div
    v-if="dealershipSelected"
    class="dealership-dashboard"
  >
    <div class="dealership-dashboard__topbar">
      <div>
        <div class="text-sm text-secondary">Dealership dashboard</div>
        <h1 class="mt-1 text-3xl font-semibold md:text-5xl">{{ selectedDealershipName }}</h1>
      </div>

      <div class="dealership-dashboard__filters">
        <Select
          v-if="dealershipsAllowed.length > 1"
          v-model="dealershipSelected"
          :options="dealershipsAllowed"
          searchable
          value-key="id"
          label-key="name"
          placeholder="Select a Dealership"
          class="min-w-[250px] shrink-0"
        />
        <div
          v-else
          class="dealership-dashboard__single-filter"
        >
          <Icon
            type="location-dot"
            class="mr-2"
          />
          <span>{{ dealershipsAllowed[0]?.name }}</span>
        </div>
        <DatePicker
          v-model="dateRange"
          class="min-w-72 shrink-0"
          selection-mode="range"
          :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
          close-on-select
          show-presets
          :presets="dateRangePresets"
          @select="onDateRangeChange"
        />
      </div>
    </div>

    <div class="dealership-dashboard__grid">
      <Card
        class="dealership-dashboard__hero"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-hero-card"
      >
        <div class="dealership-hero-card__summary">
          <div>
            <div class="text-sm uppercase tracking-[0.16em] text-white/60">Dealership cockpit</div>
            <div class="mt-2 text-3xl font-semibold">Performance snapshot</div>
            <div class="mt-3 text-sm text-white/70">{{ dateRangeLabel }}</div>
          </div>
        </div>

        <div class="dealership-hero-card__metrics">
          <div
            v-for="metric in heroMetrics"
            :key="metric.label"
            class="dealership-hero-metric"
          >
            <Icon
              :type="metric.icon"
              class="text-white/70"
            />
            <div class="mt-3 text-2xl font-semibold">{{ metric.value }}</div>
            <div class="text-xs uppercase tracking-[0.14em] text-white/60">{{ metric.label }}</div>
          </div>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__conversion"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card dealership-card--conversion"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Current conversion</div>
            <div class="text-sm text-secondary">Renewed from appointments</div>
          </div>
        </div>
        <div class="dealership-conversion-visual">
          <div class="dealership-conversion-visual__rate">{{ formatPercent(dashboardData.currentConversionRate) }}</div>
          <div class="text-sm text-secondary">{{ dashboardData.appointmentsRenewed }} renewed</div>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__actions"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Action centre</div>
            <div class="text-sm text-secondary">Items needing attention</div>
          </div>
        </div>
        <div class="dealership-row-list">
          <RouterLink
            v-for="item in actionItems"
            :key="item.label"
            :to="item.route"
            class="dealership-row"
          >
            <Avatar
              :icon="item.icon"
              :color="item.color"
              size="sm"
              no-border
            />
            <div class="min-w-0 flex-1">
              <div class="font-semibold">{{ item.label }}</div>
              <div class="text-sm text-secondary">{{ item.description }}</div>
            </div>
            <div class="dealership-count-pill">{{ item.value }}</div>
          </RouterLink>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__upcoming"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Upcoming appointments</div>
            <div class="text-sm text-secondary">Today, tomorrow, and this week</div>
          </div>
        </div>
        <div class="dealership-row-list">
          <RouterLink
            v-for="item in upcomingAppointmentRows"
            :key="item.label"
            :to="item.route"
            class="dealership-row"
          >
            <Avatar
              :icon="item.icon"
              :color="item.color"
              size="sm"
              no-border
            />
            <div class="min-w-0 flex-1">
              <div class="font-semibold">{{ item.label }}</div>
              <div class="text-sm text-secondary">{{ item.description }}</div>
            </div>
            <div class="dealership-count-pill">{{ item.value }}</div>
          </RouterLink>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__call-flow"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Call flow</div>
            <div class="text-sm text-secondary">{{ dashboardData.callsMade }} attempted calls</div>
          </div>
        </div>
        <div class="dealership-funnel dealership-funnel--call">
          <div
            v-for="metric in callFlowMetrics"
            :key="metric.label"
            :class="['dealership-funnel-stage', `dealership-funnel-stage--${metric.severity}`]"
            :style="{ '--stage-width': metric.width }"
          >
            <div class="dealership-funnel-stage__bar">
              <div class="dealership-funnel-stage__fill"></div>
            </div>
            <div class="dealership-funnel-stage__body">
              <Avatar
                :icon="metric.icon"
                :color="metric.color"
                size="sm"
                no-border
              />
              <div>
                <div class="text-2xl font-semibold">{{ metric.value }}</div>
                <div class="text-sm text-secondary">{{ metric.label }}</div>
              </div>
              <div
                v-if="metric.hint"
                class="dealership-funnel-stage__rate"
              >
                {{ metric.hint }}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__best"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card dealership-card--best"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Best performer</div>
            <div class="text-sm text-secondary">Top conversion rate</div>
          </div>
        </div>
        <div class="dealership-best-performer">
          <div class="dealership-best-performer__medal">
            <Icon type="medal" />
          </div>
          <div class="mt-5 text-3xl font-semibold">{{ dashboardData.bestPerformer?.userName ?? 'N/A' }}</div>
          <div class="mt-2 text-5xl font-semibold">{{ formatPercent(dashboardData.bestPerformer?.conversionRate ?? 0) }}</div>
          <div class="mt-2 text-sm text-secondary">leading conversion performance</div>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__outcomes"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Appointment outcomes</div>
            <div class="text-sm text-secondary">{{ formatPercent(dashboardData.currentConversionRate) }} conversion</div>
          </div>
        </div>
        <div class="dealership-funnel dealership-funnel--outcome">
          <div
            v-for="metric in appointmentTiles"
            :key="metric.label"
            :class="[
              'dealership-funnel-stage',
              `dealership-funnel-stage--${metric.color}`,
              { 'dealership-funnel-stage--highlight': metric.isHighlight }
            ]"
            :style="{ '--stage-width': metric.width }"
          >
            <div class="dealership-funnel-stage__bar">
              <div class="dealership-funnel-stage__fill"></div>
            </div>
            <div class="dealership-funnel-stage__body">
              <Avatar
                :icon="metric.icon"
                :color="metric.color"
                size="sm"
                no-border
              />
              <div>
                <div class="text-2xl font-semibold">{{ metric.value }}</div>
                <div class="text-sm text-secondary">{{ metric.label }}</div>
              </div>
              <div class="dealership-funnel-stage__rate">{{ metric.hint }}</div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        class="dealership-dashboard__trend"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="dealership-card"
      >
        <div class="dealership-card__header">
          <div>
            <div class="text-lg font-semibold">Conversion trend</div>
            <div class="text-sm text-secondary">Appointments and renewals by month</div>
          </div>
        </div>
        <apexchart
          ref="conversionHistoryChartRef"
          type="area"
          height="280"
          :options="conversionHistoryChart.options"
          :series="conversionHistoryChart.series"
        ></apexchart>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from 'vue'
import dayjs from 'dayjs'

import { formatNumber } from '@/utilities/number'
import Select from '@/components/forms/Select.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'

import Card from '@/components/card/Card.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import { useBreakPoints } from '@/composables/useWindowSize'

import Avatar from '@/components/avatar/Avatar.vue'

import apexChartDefaults from '@/components/charts/apexChartDefaults'
import Icon from '@/components/icon/Icon.vue'

const authStore = useAuthStore()
const dealershipSelected = ref('')

const dealershipStorageKey = 'dealership-dashboard-dealerships-selected'
const dateRangeStorageKey = 'dealership-dashboard-date-range'
const dealershipsAllowed = authStore.accessAllowed.dealerships
const dealershipsSelectedLS = parseInt(localStorage.getItem(dealershipStorageKey))

// Reactive effect to watch for changes in dealershipsAllowed
watchEffect(() => {
  // Ensure dealershipsAllowed is populated
  if (dealershipsAllowed && dealershipsAllowed.length > 0) {
    // Default to the first dealership if no valid selection is found in localStorage
    dealershipSelected.value = dealershipsAllowed[0].id

    if (dealershipsSelectedLS && dealershipsAllowed.some(dealership => dealership.id === dealershipsSelectedLS)) {
      dealershipSelected.value = dealershipsSelectedLS
    }
  }
})

const defaultDashboardData = {
  appointmentsToday: 0,
  callsMade: 0,
  appointmentsOccurred: 0,
  appointmentsAttended: 0,
  appointmentsRenewed: 0,
  appointmentsBooked: 0,
  valueRenewed: 0,
  appointmentsUnassigned: 0,
  appointmentsWithoutOutcome: 0,
  conversionHistory: [],
  currentConversionRate: 0,
  bestPerformer: {
    userName: null,
    conversionRate: 0
  }
}

const dashboardData = ref(defaultDashboardData)

const fetchDataRequest = useApiRequest()

const conversionHistoryChartRef = ref(null)
const conversionHistoryTooltipLabels = ref([])

const dateRange = ref(storedDateRange(dateRangeStorageKey, [
  dayjs().startOf('month').format('YYYY-MM-DD'),
  dayjs().startOf('day').format('YYYY-MM-DD')
]))
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  }
]

const selectedDealershipName = computed(() => {
  return dealershipsAllowed.find(dealership => dealership.id === dealershipSelected.value)?.name ?? 'Dealership'
})

const dateRangeLabel = computed(() => {
  if (!dateRange.value?.[0] || !dateRange.value?.[1]) {
    return 'Selected range'
  }

  return `${dayjs(dateRange.value[0]).format('Do MMM YYYY')} - ${dayjs(dateRange.value[1]).format('Do MMM YYYY')}`
})

const heroMetrics = computed(() => [
  {
    label: 'Revenue',
    value: formatNumber(dashboardData.value.valueRenewed, 'currency', 0) ?? '£0',
    icon: 'sterling-sign'
  },
  {
    label: 'Renewed',
    value: dashboardData.value.appointmentsRenewed,
    icon: 'check'
  },
  {
    label: 'Booked',
    value: dashboardData.value.appointmentsBooked,
    icon: 'calendar-plus'
  }
])

const actionItems = computed(() => [
  {
    label: 'Unassigned appointments',
    description: 'Need an owner',
    value: dashboardData.value.appointmentsUnassigned,
    icon: 'user-clock',
    color: 'warning',
    route: `/appointments/manager?dealerships=${dealershipSelected.value}&actionStatus=unassigned`
  },
  {
    label: 'Missing outcomes',
    description: 'Need appointment outcome',
    value: dashboardData.value.appointmentsWithoutOutcome,
    icon: 'clipboard-list',
    color: 'brand',
    route: `/appointments/manager?dealerships=${dealershipSelected.value}`
  }
])

const upcomingAppointmentRows = computed(() => [
  {
    label: 'Today',
    description: dayjs().format('Do MMM YYYY'),
    value: dashboardData.value.appointmentsToday,
    icon: 'calendar-day',
    color: 'brand',
    route: `/appointments/calendar?view=timeGridDay&date=${dayjs().format('YYYY-MM-DD')}`
  },
  {
    label: 'Tomorrow',
    description: dayjs().add(1, 'day').format('Do MMM YYYY'),
    value: dashboardData.value.appointmentsTomorrow,
    icon: 'calendar-plus',
    color: 'info',
    route: `/appointments/calendar?view=timeGridDay&date=${dayjs().add(1, 'day').format('YYYY-MM-DD')}`
  },
  {
    label: 'This week',
    description: `${dayjs().startOf('week').format('Do MMM')} - ${dayjs().endOf('week').format('Do MMM')}`,
    value: dashboardData.value.appointmentsThisWeek,
    icon: 'calendar-week',
    color: 'success',
    route: `/appointments/calendar?view=timeGridWeek&date=${dayjs().format('YYYY-MM-DD')}`
  }
])

const callFlowMetrics = computed(() => [
  {
    label: 'Calls made',
    value: dashboardData.value.callsMade,
    icon: 'phone-arrow-up-right',
    color: 'brand',
    severity: 'brand',
    hint: '100%',
    width: '100%'
  },
  {
    label: 'Answered',
    value: dashboardData.value.callsAnswered,
    icon: 'phone-volume',
    color: 'info',
    severity: 'info',
    hint: formatPercent(ratioOf(dashboardData.value.callsAnswered, dashboardData.value.callsMade)),
    width: percentWidth(dashboardData.value.callsAnswered, dashboardData.value.callsMade)
  },
  {
    label: 'Opportunity',
    value: dashboardData.value.callsOpportunities,
    icon: 'user-check',
    color: 'warning',
    severity: 'warning',
    hint: formatPercent(ratioOf(dashboardData.value.callsOpportunities, dashboardData.value.callsMade)),
    width: percentWidth(dashboardData.value.callsOpportunities, dashboardData.value.callsMade)
  },
  {
    label: 'Booked',
    value: dashboardData.value.appointmentsBooked,
    icon: 'calendar-plus',
    color: 'success',
    severity: 'success',
    hint: formatPercent(ratioOf(dashboardData.value.appointmentsBooked, dashboardData.value.callsMade)),
    width: percentWidth(dashboardData.value.appointmentsBooked, dashboardData.value.callsMade)
  }
])

const appointmentTiles = computed(() => [
  {
    label: 'Occurred',
    value: dashboardData.value.appointmentsOccurred,
    icon: 'calendar',
    color: 'brand',
    hint: '100%',
    width: '100%',
    isHighlight: false
  },
  {
    label: 'Attended',
    value: dashboardData.value.appointmentsAttended,
    icon: 'user',
    color: 'info',
    hint: formatPercent(ratioOf(dashboardData.value.appointmentsAttended, dashboardData.value.appointmentsOccurred)),
    width: percentWidth(dashboardData.value.appointmentsAttended, dashboardData.value.appointmentsOccurred),
    isHighlight: false
  },
  {
    label: 'Renewed',
    value: dashboardData.value.appointmentsRenewed,
    icon: 'check',
    color: 'success',
    hint: formatPercent(ratioOf(dashboardData.value.appointmentsRenewed, dashboardData.value.appointmentsOccurred)),
    width: percentWidth(dashboardData.value.appointmentsRenewed, dashboardData.value.appointmentsOccurred),
    isHighlight: true
  },
  {
    label: 'No-show',
    value: dashboardData.value.appointmentsNoShow,
    icon: 'xmark',
    color: 'danger',
    hint: formatPercent(ratioOf(dashboardData.value.appointmentsNoShow, dashboardData.value.appointmentsOccurred)),
    width: percentWidth(dashboardData.value.appointmentsNoShow, dashboardData.value.appointmentsOccurred),
    isHighlight: false
  }
])

function getDashboardData() {
  if (!dealershipSelected.value || !dateRange.value[0] || !dateRange.value[1]) {
    return
  }
  // Reset the dashboard data
  dashboardData.value = defaultDashboardData

  fetchDataRequest
    .send({
      endpoint: '/v1/dashboards/dealership',
      method: 'get',
      params: {
        dealership: dealershipSelected.value,
        start: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
        end: dayjs(dateRange.value[1]).format('YYYY-MM-DD')
      }
    })
    .then(response => {
      dashboardData.value = response.data

      const categories = []
      const appointments = []
      const renewals = []
      conversionHistoryTooltipLabels.value = []

      dashboardData.value.conversionHistory.forEach(month => {
        const shortMonth = formatConversionMonth(month.month, 'MMM')

        categories.push(shortMonth)
        conversionHistoryTooltipLabels.value.push(formatConversionMonth(month.month, 'MMMM YYYY'))
        appointments.push({
          x: shortMonth,
          y: month.total
        })
        renewals.push({
          x: shortMonth,
          y: month.renewed
        })
      })

      conversionHistoryChart.value.series = [
        {
          name: 'Appointments',
          data: appointments
        },
        {
          name: 'Renewals',
          data: renewals
        }
      ]

      conversionHistoryChart.value.options = {
        ...conversionHistoryChart.value.options,
        xaxis: getConversionHistoryXAxis(categories),
        tooltip: getConversionHistoryTooltip()
      }

      if (conversionHistoryChartRef.value) {
        conversionHistoryChartRef.value.updateOptions(conversionHistoryChart.value.options, false, true)
        conversionHistoryChartRef.value.updateSeries(conversionHistoryChart.value.series, true)
      }
    })
}
getDashboardData()

// fetch the data when the selected dealership changes
watch(
  dealershipSelected,
  () => {
    getDashboardData()
    // Store selected dealership in local storage
    localStorage.setItem(dealershipStorageKey, dealershipSelected.value)
  },
  { deep: true }
)

const conversionHistoryChart = ref({
  ...apexChartDefaults.lineAreaChartSpline,
  options: {
    ...apexChartDefaults.lineAreaChartSpline.options,
    colors: ['#2563EB', '#12B76A'],
    fill: {
      opacity: [0.35, 0.65],
      type: 'solid'
    },
    tooltip: getConversionHistoryTooltip(),
    grid: {
      ...apexChartDefaults.lineAreaChartSpline.options.grid,
      padding: {
        ...apexChartDefaults.lineAreaChartSpline.options.grid.padding,
        bottom: 0
      }
    },
    xaxis: getConversionHistoryXAxis([])
  },
  series: [
    {
      name: 'Appointments',
      data: []
    },
    {
      name: 'Renewals',
      data: []
    }
  ]
})

function onDateRangeChange() {
  localStorage.setItem(dateRangeStorageKey, JSON.stringify(dateRange.value))
  getDashboardData()
}

function storedDateRange(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null')

    if (Array.isArray(value) && value.length === 2 && value.every(item => typeof item === 'string')) {
      return value
    }
  } catch {
    return fallback
  }

  return fallback
}

function formatPercent(value) {
  return `${formatNumber((value ?? 0) * 100, 'normal', 1) ?? '0'}%`
}

function formatConversionMonth(value, format) {
  const stringValue = String(value ?? '')
  const parsedDate = dayjs(stringValue)

  if (parsedDate.isValid() && /\d/.test(stringValue)) {
    return parsedDate.format(format)
  }

  return stringValue || 'N/A'
}

function getConversionHistoryXAxis(categories) {
  return {
    type: 'category',
    categories,
    tickPlacement: 'on',
    axisBorder: {
      show: true,
      color: 'var(--border-secondary)'
    },
    axisTicks: {
      show: true,
      color: 'var(--border-secondary)'
    },
    tooltip: {
      enabled: false
    },
    labels: {
      show: true,
      rotate: 0,
      trim: false,
      hideOverlappingLabels: false,
      style: {
        colors: 'var(--text-secondary)'
      }
    }
  }
}

function getConversionHistoryTooltip() {
  return {
    enabled: true,
    shared: true,
    intersect: false,
    custom: ({ series, dataPointIndex, w }) => {
      const label = conversionHistoryTooltipLabels.value[dataPointIndex] ?? w.globals.labels[dataPointIndex] ?? 'N/A'
      const appointments = formatNumber(series[0]?.[dataPointIndex] ?? 0, 'normal', 0)
      const renewals = formatNumber(series[1]?.[dataPointIndex] ?? 0, 'normal', 0)

      return `
        <div class="px-3 py-2">
          <div class="mb-2 text-sm font-semibold">${label}</div>
          <div class="flex items-center gap-2 text-xs">
            <span style="background:#2563EB;width:8px;height:8px;border-radius:999px;display:inline-block;"></span>
            <span>Appointments: <strong>${appointments}</strong></span>
          </div>
          <div class="mt-1 flex items-center gap-2 text-xs">
            <span style="background:#12B76A;width:8px;height:8px;border-radius:999px;display:inline-block;"></span>
            <span>Renewals: <strong>${renewals}</strong></span>
          </div>
        </div>
      `
    }
  }
}

function ratioOf(value, total) {
  if (!total) {
    return 0
  }

  return value / total
}

function percentWidth(value, total) {
  return `${Math.max(Math.min(ratioOf(value, total) * 100, 100), value ? 8 : 0)}%`
}

</script>

<style scoped>
.dealership-dashboard {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-height: calc(100vh - var(--s-8));
}

.dealership-dashboard__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4);
}

.dealership-dashboard__filters {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  justify-content: flex-end;
  gap: var(--s-2);
  min-width: max-content;
  overflow-x: auto;
  white-space: nowrap;
}

.dealership-dashboard__single-filter {
  display: flex;
  align-items: center;
  border-radius: var(--rounded-full);
  padding: var(--s-2) var(--s-4);
  background: rgba(255, 255, 255, 0.68);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  box-shadow: 0 12px 36px rgba(16, 24, 40, 0.08);
  backdrop-filter: blur(14px);
}

.dealership-dashboard__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--s-4);
  align-items: start;
}

.dealership-dashboard__hero,
.dealership-dashboard__call-flow,
.dealership-dashboard__outcomes,
.dealership-dashboard__trend {
  grid-column: span 2;
}

.dealership-dashboard__conversion,
.dealership-dashboard__actions,
.dealership-dashboard__upcoming,
.dealership-dashboard__best {
  grid-column: span 1;
}

.dealership-dashboard :deep(.card) {
  border-radius: 2rem;
}

:deep(.dealership-hero-card),
:deep(.dealership-card) {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.07);
  backdrop-filter: blur(18px);
}

:deep(.dealership-hero-card) {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: var(--s-5);
  padding: var(--s-5);
  color: white;
  background:
    linear-gradient(180deg, rgba(30, 41, 59, 0.04), rgba(30, 41, 59, 0.62)),
    radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.26), transparent 11rem),
    linear-gradient(135deg, #60a5fa, #c084fc 54%, #fda4af);
}

:deep(.dealership-card) {
  height: 100%;
  padding: var(--s-5);
}

.dealership-hero-card__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--s-3);
}

.dealership-hero-card__summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-4);
}

.dealership-hero-metric,
.dealership-metric-tile,
.dealership-row {
  border-radius: 1.35rem;
  padding: var(--s-4);
  background: rgba(255, 255, 255, 0.62);
}

.dealership-hero-metric {
  background: rgba(255, 255, 255, 0.14);
}

.dealership-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-3);
  margin-bottom: var(--s-4);
}

.dealership-row-list {
  display: flex;
  flex-direction: column;
  gap: var(--s-3);
}

.dealership-row {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  color: inherit;
}

.dealership-count-pill {
  border-radius: var(--rounded-full);
  padding: var(--s-1) var(--s-3);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 600;
}

:deep(.dealership-card--conversion) {
  background:
    radial-gradient(circle at 80% 14%, rgba(147, 197, 253, 0.2), transparent 12rem),
    rgba(255, 255, 255, 0.76);
}

:deep(.dealership-card--best) {
  background:
    radial-gradient(circle at 50% 8%, rgba(251, 191, 36, 0.2), transparent 11rem),
    rgba(255, 255, 255, 0.78);
}

.dealership-conversion-visual {
  display: flex;
  min-height: 8.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.62);
  text-align: center;
}

.dealership-conversion-visual__rate {
  font-size: clamp(3rem, 7vw, 5.5rem);
  font-weight: 700;
  line-height: 1;
}

.dealership-best-performer {
  display: flex;
  min-height: 14rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.dealership-best-performer__medal {
  display: flex;
  width: 4.5rem;
  height: 4.5rem;
  align-items: center;
  justify-content: center;
  border-radius: var(--rounded-full);
  background: linear-gradient(135deg, #fef3c7, #f59e0b);
  color: #78350f;
  font-size: 2rem;
  box-shadow: 0 18px 42px rgba(245, 158, 11, 0.22);
}

.dealership-funnel {
  display: grid;
  gap: var(--s-3);
}

.dealership-funnel--call {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.dealership-funnel--outcome {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.dealership-funnel-stage {
  position: relative;
  overflow: hidden;
  min-height: 12rem;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.56);
}

.dealership-funnel-stage__bar {
  position: absolute;
  inset: auto var(--s-4) var(--s-4);
  height: 4rem;
  overflow: hidden;
  border-radius: 1rem;
  background: rgba(148, 163, 184, 0.12);
}

.dealership-funnel-stage__fill {
  width: var(--stage-width);
  height: 100%;
  border-radius: inherit;
  background:
    repeating-linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.28) 0,
      rgba(255, 255, 255, 0.28) 0.45rem,
      transparent 0.45rem,
      transparent 0.85rem
    ),
    var(--stage-fill, linear-gradient(135deg, #60a5fa, #6366f1));
  box-shadow: 0 18px 40px var(--stage-shadow, rgba(96, 165, 250, 0.22));
}

.dealership-funnel-stage--highlight .dealership-funnel-stage__fill {
  box-shadow: 0 18px 44px rgba(16, 185, 129, 0.28);
}

.dealership-funnel-stage__body {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 100%;
  flex-direction: column;
  justify-content: flex-start;
  gap: var(--s-3);
  padding: var(--s-4) var(--s-4) calc(4rem + var(--s-7));
}

.dealership-funnel-stage__rate {
  position: absolute;
  bottom: calc(var(--s-4) + 0.7rem);
  left: var(--s-4);
  z-index: 2;
  border-radius: var(--rounded-full);
  padding: var(--s-1) var(--s-3);
  background: rgba(255, 255, 255, 0.68);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: 600;
}

.dealership-funnel-stage--brand {
  --stage-fill: linear-gradient(135deg, #60a5fa, #6366f1);
  --stage-shadow: rgba(96, 165, 250, 0.22);
}

.dealership-funnel-stage--info {
  --stage-fill: linear-gradient(135deg, #38bdf8, #2563eb);
  --stage-shadow: rgba(56, 189, 248, 0.22);
}

.dealership-funnel-stage--warning {
  --stage-fill: linear-gradient(135deg, #fbbf24, #f97316);
  --stage-shadow: rgba(251, 191, 36, 0.22);
}

.dealership-funnel-stage--success {
  --stage-fill: linear-gradient(135deg, #34d399, #10b981);
  --stage-shadow: rgba(16, 185, 129, 0.24);
}

.dealership-funnel-stage--danger {
  --stage-fill: linear-gradient(135deg, #fb7185, #ef4444);
  --stage-shadow: rgba(239, 68, 68, 0.22);
}

.dark .dealership-dashboard__single-filter,
.dark :deep(.dealership-card) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

.dark :deep(.dealership-hero-card) {
  border-color: rgba(255, 255, 255, 0.1);
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0.72)),
    radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.12), transparent 11rem),
    linear-gradient(135deg, #1e3a8a, #581c87 58%, #881337);
}

.dark .dealership-metric-tile,
.dark .dealership-row,
.dark .dealership-conversion-visual,
.dark .dealership-funnel-stage {
  background: rgba(255, 255, 255, 0.06);
}

.dark .dealership-funnel-stage__rate {
  background: rgba(255, 255, 255, 0.08);
}

.dark :deep(.dealership-card--conversion) {
  background:
    radial-gradient(circle at 80% 14%, rgba(147, 197, 253, 0.12), transparent 12rem),
    rgba(17, 24, 39, 0.78);
}

.dark :deep(.dealership-card--best) {
  background:
    radial-gradient(circle at 50% 8%, rgba(251, 191, 36, 0.14), transparent 11rem),
    rgba(17, 24, 39, 0.78);
}

@media (max-width: 1280px) {
  .dealership-dashboard__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dealership-dashboard__hero,
  .dealership-dashboard__call-flow,
  .dealership-dashboard__outcomes,
  .dealership-dashboard__trend {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .dealership-dashboard__topbar {
    flex-direction: column;
    align-items: stretch;
    overflow-x: auto;
  }

  .dealership-dashboard__grid,
  .dealership-funnel--call,
  .dealership-funnel--outcome,
  .dealership-hero-card__metrics {
    grid-template-columns: 1fr;
  }

  .dealership-hero-card__summary {
    flex-direction: column;
  }

  .dealership-dashboard__hero,
  .dealership-dashboard__call-flow,
  .dealership-dashboard__outcomes,
  .dealership-dashboard__trend,
  .dealership-dashboard__conversion,
  .dealership-dashboard__actions,
  .dealership-dashboard__upcoming,
  .dealership-dashboard__best {
    grid-column: span 1;
  }
}
</style>
